import React from "react";

import Link from "next/link";

import {
  Anchor,
  ArticleCard,
  AudioCard,
  Badge,
  BiographyCard,
  Button,
  Chip,
  ChipGroup,
  CollectionCard,
  ContentCard,
  ExternalLinkCard,
  Icon,
  InfographicCard,
  LessonSparkCard,
  LineClamp,
  PollCard,
  PowerWordFilledSVG,
  SearchSVG,
  SpanishFilledSVG,
  StandardCard,
  TextSetCard,
  VideoCard,
  WorksheetCard,
} from "@newsela/angelou";
import { isUndefined } from "@s-libs/micro-dash";
import { useRouter } from "next/router";
import PropTypes from "prop-types";

import CardInfoIcons from "@/components/CardInfoIcons";
import generateCardEventStreamProps from "@/eventStream/generateCardEventStreamProps";
import generateOulipoEventStreamProps from "@/eventStream/generateOulipoEventStreamProps";
import withTrackEvent from "@/eventStream/withTrackEvent";
import ExternalLinkIcon from "@/icons/ExternalLinkIcon";
import FormativeIcon from "@/icons/FormativeIcon/FormativeIcon";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";
import convertToImgixUrl from "@/utils/convertToImgixUrl";
import { shapeImgixParams } from "@/utils/convertToImgixUrl/convertToImgixUrl.constants";
import formatDate from "@/utils/formatDate";
import getCardDataQaSelector from "@/utils/getCardQaDataSelector/getCardQaDataSelector";
import getContentLabel from "@/utils/getContentLabel";
import getFallbackImage from "@/utils/getFallbackImage";
import getHref from "@/utils/getHref";
import KnowledgeEntityCard from "../KnowledgeEntityCard";
import BookmarkButton from "../BookmarkButton";
import {
  bookmarkWrapperCss,
  contentCardCss,
  contentCardTextualCss,
  contentCardTextualDateCss,
  contentTypeContainerCss,
  contentTypeLeft,
  contentTypeRight,
  customContentTypeAndFeaturesRowCss,
  externalLinkCardFooterCss,
  externalLinkIconCss,
  kindOfTopicCss,
  lastestVideoCardCss,
  queryContentCardCss,
  queryContentCss,
  queryContentSearchIconCss,
  queryContentTitleCss,
  readingSkillsChipsCss,
  readingSkillsChipsGroupCss,
  readingSkillsCss,
  titleCss,
  topTextCss,
} from "./DeckOfCards.style";

const ArticleCardWithTrackEvent = withTrackEvent(ArticleCard);
const AudioCardWithTrackEvent = withTrackEvent(AudioCard);
const BiographyCardWithTrackEvent = withTrackEvent(BiographyCard);
const CollectionCardWithTrackEvent = withTrackEvent(CollectionCard);
const ExternalLinkCardWithTrackEvent = withTrackEvent(ExternalLinkCard);
const InfographicCardWithTrackEvent = withTrackEvent(InfographicCard);
const LessonSparkCardWithTrackEvent = withTrackEvent(LessonSparkCard);
const KnowledgeEntityCardWithTrackEvent = withTrackEvent(KnowledgeEntityCard);
const PollCardWithTrackEvent = withTrackEvent(PollCard);
const StandardCardWithTrackEvent = withTrackEvent(StandardCard);
const TextSetCardWithTrackEvent = withTrackEvent(TextSetCard);
const VideoCardWithTrackEvent = withTrackEvent(VideoCard);
const WorksheetCardWithTrackEvent = withTrackEvent(WorksheetCard);
const ContentCardWithTrackEvent = withTrackEvent(ContentCard);

const getCardImage = ({
  thumbnail,
  image_url: imageUrl,
  image,
  content_id: contentId,
}) => {
  return thumbnail || imageUrl || image?.url || getFallbackImage(contentId);
};

const renderImageAltText = (props) => {
  // Renders default alt text for images as 'Article Image' if alt text is null, missing, or 'An image.'
  return Boolean(props?.image?.alt_text) === false ||
    props.image?.alt_text === "An image."
    ? "Article Image"
    : props.image.alt_text;
};

const useConvertAPINamesToCardPropNames = (props) => {
  const uncomfortableHistory = props.veil?.reason === "DISTRICT_ADVISORY";

  return {
    ...props,
    assetCount: props.member_count,
    duration: props.length_in_seconds,
    datePublished: props.date_published,
    href: getHref(props),
    img: {
      src: convertToImgixUrl(getCardImage(props)),
      alt: renderImageAltText(props),
      imgixParams: shapeImgixParams(props.image),
    },
    ...(uncomfortableHistory && {
      badgeProps: {
        dataProps: {
          "data-qa-selector": "cat_label",
        },
      },
    }),
    title: props.link_title || props.name,
    uncomfortableHistory,
  };
};

const getVisualProperties = (props) => {
  const { isVertical } = props;
  const visualProperties = {
    isVertical: isUndefined(isVertical) ? true : isVertical,
  };
  return visualProperties;
};

const getContentType = (props) => {
  const renderFunction = (passedProps) => {
    return (
      <div
        className={passedProps.className}
        css={[passedProps.css, contentTypeContainerCss]}
      >
        {getContentLabel(props)}
        {props.openInNewTab && <ExternalLinkIcon css={externalLinkIconCss} />}
      </div>
    );
  };
  return renderFunction;
};

const getBadges = (props, isLiteUser, isStudent) => {
  const badges = [];
  const visibilityMarkers = props?.visibility_markers ?? [];
  let advisoryBadgeApplied = false;

  for (const visibilityMarker of visibilityMarkers) {
    switch (visibilityMarker.reason) {
      case "DISTRICT_ADVISORY":
      case "SCHOOL_ADVISORY":
        if (!advisoryBadgeApplied) {
          if (visibilityMarker.flagged_at_my_admin_level) {
            badges.push({
              color: Badge.color.yellow,
              flavor: Badge.flavor.solid,
              text: "Flagged",
            });
          } else {
            badges.push({
              color: Badge.color.yellow,
              flavor: Badge.flavor.solid,
              text: "Advisory",
            });
          }
          // It is possible to have multiple advisory visibility markers, but we only want one badge applied.
          advisoryBadgeApplied = true;
        }
        break;
      case "LITE_CONTENT":
        if (isLiteUser) {
          const expirationDate =
            visibilityMarker.date_lite_product_grant_expires;
          const isExpiredDate = new Date() > new Date(expirationDate);
          if (!!expirationDate && !isExpiredDate) {
            badges.push({
              flavor: Badge.flavor.flat,
              text:
                "Read until " + new Date(expirationDate).toLocaleDateString(),
            });
          }
        }
        break;
      case "PREMIUM_CONTENT":
        if (isLiteUser && !isStudent) {
          badges.push({
            flavor: Badge.flavor.solid,
            text: "Premium",
          });
        }
    }
  }

  // This checks for is_lite property in the v3 content and content document APIs
  if (isLiteUser && visibilityMarkers?.length === 0) {
    const isLiteContent = props.is_lite;
    const expirationDate = props.date_lite_product_grant_expires;
    const isExpiredDate = new Date() > new Date(expirationDate);
    if (isLiteContent) {
      if (!!expirationDate && !isExpiredDate) {
        badges.push({
          flavor: Badge.flavor.flat,
          text: "Read until " + new Date(expirationDate).toLocaleDateString(),
        });
      }
    } else if (!isStudent) {
      badges.push({ flavor: Badge.flavor.solid, text: "Premium" });
    }
  }
  return badges;
};

const propTypes = {
  author: PropTypes.string,
  dataQaSelector: PropTypes.string,
  date_published: PropTypes.string,
  date_created: PropTypes.string,
  block_type: PropTypes.string,
  content_id: PropTypes.string,
  content_provider: PropTypes.shape({
    name: PropTypes.string,
  }),
  className: PropTypes.string,
  content_type: PropTypes.string,
  dataProps: PropTypes.object,
  footer: PropTypes.node,
  search_id: PropTypes.string,
  has_formative: PropTypes.object,
  href: PropTypes.string,
  index: PropTypes.number,
  label: PropTypes.string,
  member_count: PropTypes.number,
  onClick: PropTypes.func,
  openInNewTab: PropTypes.bool,
  parentSlug: PropTypes.string,
  source: PropTypes.string,
  isSortableItem: PropTypes.bool,
  streamProperties: PropTypes.object,
  url: PropTypes.string,
  isStacked: PropTypes.bool,
  badges: PropTypes.array,
  visibility_markers: PropTypes.array,
  seasonalMultiEntity: PropTypes.bool,
  isLatestVideo: PropTypes.bool,
  popularContent: PropTypes.bool,
  reading_skills: PropTypes.object,
  extra_fields: PropTypes.object,
  features: PropTypes.object,
  title: PropTypes.string,
};

// cropping threshold; this was tested with multiple images and images above the threshold were
// fine, while images nearing the threshold were cropped incorrectly.
const CROPPING_THRESHOLD = 0.31;

const DeckOfCards = React.forwardRef((props, ref) => {
  const {
    dataQaSelector,
    index,
    isSortableItem = false,
    openInNewTab = false,
    streamProperties,
    seasonalMultiEntity,
    isLatestVideo,
    popularContent,
  } = props;

  const { data: user } = useUser();
  const userProperties = getUserProperties(user);
  const { isLiteUser, isStudent, activeELALicense, canSeeFormative } =
    userProperties;

  const { pathname } = useRouter();
  const displayReadingSkillsChips = activeELALicense || isLiteUser;

  const shouldCropToTop = ({ image }) => {
    return image?.fp_y && image?.fp_y <= CROPPING_THRESHOLD;
  };
  const getContentTypeLabel = () => {
    const contentTypeLabel = {
      articleheader: "Article",
      interactivevideolink: "Interactive video",
      textset: "Text set",
      generation_genius_video: "Generation Genius",
    };

    return contentTypeLabel[props.content_type];
  };
  const getReadingSkillsValues = () => {
    if (!props.reading_skills && !props.extra_fields?.reading_skills) {
      return;
    }
    const readingSkillsSet = props.reading_skills
      ? props.reading_skills
      : props.extra_fields.reading_skills;

    const readingSkillsCard = readingSkillsSet?.filter((item) =>
      item.includes("essential"),
    );

    const visualReadingSkills = {
      "essentials-reading-skills-what-the-text-says": "What the text says",
      "essentials-reading-skills-main-idea":
        "Main idea, key details & summarization",
      "essentials-reading-skills-key-details":
        "Main idea, key details & summarization",
      "essentials-reading-skills-summarization":
        "Main idea, key details & summarization",
      "essentials-reading-skills-connecting-people-events-ideas":
        "Connecting people, events & ideas",
      "essentials-reading-skills-word-meaning-choice": "Word meaning & choice",
      "essentials-reading-skills-text-structure": "Text structure",
      "essentials-reading-skills-point-of-view-purpose":
        "Point of view & purpose",
      "essentials-reading-skills-interpret-multimedia": "Interpret multimedia",
      "essentials-reading-skills-arguments-claims": "Arguments & claims",
      "essentials-reading-skills-theme": "Theme",
    };

    const values = [
      ...new Set(readingSkillsCard.map((item) => visualReadingSkills[item])),
    ].sort();
    const response = values.length > 0 ? values : readingSkillsSet;
    return response;
  };

  const RenderReadingSkillsChips = () => {
    return (
      <div css={contentCardTextualCss}>
        <div css={readingSkillsCss}>
          <div css={topTextCss}>
            <div css={kindOfTopicCss}>
              <div css={contentTypeLeft}>
                {props.label ? props.label : getContentTypeLabel()}
              </div>
              <div css={contentTypeRight}>
                {props.features?.has_power_words && (
                  <Icon
                    title="Contains Power Word Activity"
                    SvgComponent={PowerWordFilledSVG}
                  />
                )}
                {props.features?.available_in_spanish && (
                  <Icon
                    title="Also available in Spanish"
                    SvgComponent={SpanishFilledSVG}
                  />
                )}
                {canSeeFormative &&
                  !!props.has_formative?.data &&
                  Object.keys(props.has_formative.data).length > 0 && (
                    <Icon
                      title="Formative available"
                      SvgComponent={FormativeIcon}
                    />
                  )}
              </div>
            </div>
            <div css={titleCss}>{props.title}</div>
          </div>

          <div css={readingSkillsChipsCss}>
            {getReadingSkillsValues() && (
              <ChipGroup
                chipType="filter"
                groupName="reading skills"
                size="sm"
                __cssFor={readingSkillsChipsGroupCss}
              >
                {getReadingSkillsValues()
                  .slice(0, 3)
                  ?.map((item, index) => {
                    return <Chip key={index} text={item} value={item} />;
                  })}
              </ChipGroup>
            )}
          </div>
        </div>
        <div css={contentCardTextualDateCss}>
          {formatDate(new Date(props.date_published), "mm/dd/yyyy")}
        </div>
      </div>
    );
  };

  const renderTextualProps = displayReadingSkillsChips
    ? {
        __renderTextual: () => (
          <RenderReadingSkillsChips contentTypeLabel={props.label} />
        ),
      }
    : {};

  // Note: using streamProperties implies that the personalized home page is serving data
  const eventStreamProps = streamProperties
    ? generateOulipoEventStreamProps({
        ...streamProperties,
        ...props,
        index,
        parentSlug: "",
        referrer: pathname,
      })
    : generateCardEventStreamProps({ ...props, referrer: pathname });
  const { streamProperties: parentStreamProperties, ...otherProps } = props;
  const combinedStreamProperties = {
    ...parentStreamProperties,
    ...eventStreamProps,
  };
  const internalDataProps = {
    "data-qa-selector": `${getCardDataQaSelector(props)} ${
      dataQaSelector || ""
    }`,
    onContextMenu: (e) => {
      if (e.button === 2) {
        if ("heap" in globalThis) {
          globalThis.heap.track("right click content card", {
            link: props.href,
          });
        }
      }
    },
    onAuxClick: (e) => {
      if (e.button === 1) {
        if ("heap" in globalThis) {
          globalThis.heap.track("aux click content card", {
            link: props.href,
          });
        }
      }
    },
  };
  const dataProps = otherProps.dataProps
    ? {
        ...internalDataProps,
        ...otherProps.dataProps,
      }
    : internalDataProps;
  const mappedProps = {
    ...getVisualProperties(props),
    ...useConvertAPINamesToCardPropNames(props),
    __renderContentType: getContentType(props),
    ...(isSortableItem && props?.className
      ? {
          classes: { root: props?.className },
        }
      : {}),
    __css: props.href ? null : contentCardCss(shouldCropToTop(props)),
    badges: getBadges(props, isLiteUser, isStudent),
    __renderCustomLink: props.href
      ? (linkProps) => (
          <Link href={props.href} passHref legacyBehavior>
            <Anchor
              ariaProps={{
                "aria-label": `View ${
                  linkProps["aria-label"]
                }. Link opens in a new tab`,
              }}
              openInNewTab={openInNewTab}
              __cssFor={{
                root: linkProps.css,
              }}
            />
          </Link>
        )
      : null,
    ...combinedStreamProperties,
    ...otherProps,
    dataProps,
    ref, // The ref is for data tracking on component when wrapped with the tracking HOC
  };

  const getStoryType = (contentType = "Article", storyType) => {
    /**
     * The story types ['news', 'editorial', 'biography', 'information'] are concealed
     * so that article header categories are displayed as labels instead.
     * The storyTypesToDisplayArray includes all story types that will appear as labels,
     * as per current content team guidance.
     */
    const storyTypesToDisplay = [
      "fiction",
      "infographic",
      "map",
      "photo",
      "poem",
      "drama",
      "biography",
    ];
    return storyTypesToDisplay.includes(storyType) ? storyType : contentType;
  };

  const renderContentType = (mappedProps, contentType) => {
    const { story_type: storyType, features } = mappedProps;
    const label = getStoryType(contentType, storyType);
    return (
      <div css={customContentTypeAndFeaturesRowCss}>
        {label}
        <CardInfoIcons features={features} userProperties={userProperties} />
      </div>
    );
  };

  const stackedDeck = {
    articleheader: () => {
      <ContentCardWithTrackEvent
        {...mappedProps}
        __renderContentType={(angelouProps, contentType) =>
          renderContentType(mappedProps, contentType)
        }
      />;
    },
  };

  const deck = {
    querycontent: () => {
      const { title, contentId, streamProperties, ...passThroughProps } =
        mappedProps;
      return (
        <ContentCardWithTrackEvent
          {...passThroughProps}
          __css={queryContentCardCss(seasonalMultiEntity)}
          ref={ref}
          title={title}
          anchorAriaProps={{
            "aria-label": `Search results for ${title}`,
          }}
          __renderTextual={() => (
            <div css={queryContentCss}>
              {!seasonalMultiEntity && (
                <Icon
                  SvgComponent={SearchSVG}
                  __cssFor={{ root: queryContentSearchIconCss }}
                />
              )}
              <LineClamp
                text={title}
                lines={1}
                innerElement="h3"
                css={queryContentTitleCss(seasonalMultiEntity)}
              />
            </div>
          )}
          footer=""
        />
      );
    },
    lastestVideo: () => {
      const { streamProperties } = mappedProps;
      return mappedProps?.block_type === "video_card" ? (
        <VideoCardWithTrackEvent
          {...mappedProps}
          __css={lastestVideoCardCss(popularContent)}
          footer={
            <BookmarkButton
              contentId={mappedProps.content_id}
              iconColor={popularContent ? "" : "white"}
              __cssFor={{
                root: { bookmarkWrapperCss },
              }}
              streamProperties={streamProperties}
            />
          }
        />
      ) : (
        <ArticleCardWithTrackEvent
          {...mappedProps}
          __css={lastestVideoCardCss(popularContent)}
          footer={
            <BookmarkButton
              contentId={mappedProps.content_id}
              iconColor={popularContent ? "" : "white"}
              __cssFor={{
                root: { bookmarkWrapperCss },
              }}
              streamProperties={streamProperties}
            />
          }
        />
      );
    },
    articleheader: () => (
      <ArticleCardWithTrackEvent
        {...mappedProps}
        __renderContentType={(angelouProps, contentType) =>
          renderContentType(mappedProps, contentType)
        }
        {...renderTextualProps}
      />
    ),
    articlerecommendationexperiment: () => (
      <ArticleCardWithTrackEvent
        {...mappedProps}
        __renderContentType={(angelouProps, contentType) =>
          renderContentType(mappedProps, contentType)
        }
      />
    ),
    articlestory: () => (
      <ArticleCardWithTrackEvent
        {...mappedProps}
        __renderContentType={(angelouProps, contentType) =>
          renderContentType(mappedProps, contentType)
        }
      />
    ),
    audiotrack: () => <AudioCardWithTrackEvent {...mappedProps} />,
    biography: () => (
      <BiographyCardWithTrackEvent {...renderTextualProps} {...mappedProps} />
    ),
    collection: () => (
      <CollectionCardWithTrackEvent {...renderTextualProps} {...mappedProps} />
    ),
    entity: () => (
      <KnowledgeEntityCardWithTrackEvent
        {...renderTextualProps}
        {...mappedProps}
      />
    ),
    externallink: () => (
      <ExternalLinkCardWithTrackEvent
        {...mappedProps}
        source={props?.content_provider?.name || props?.author}
        datePublished={props?.date_published || props?.date_created}
        footer={
          <div css={externalLinkCardFooterCss}>
            <Anchor
              href={props.url}
              openInNewTab
              icon={{ isIconBeforeText: true, removeTextUnderline: false }}
              styleAnchorAsButton={{
                /* AUTOGENERATED TODO: update angelou to new flavor.
                  see https://github.com/newsela/angelou/blob/main/src/components/Button/README.md#MIGRATION
                  for migration guide. */
                legacy_flavor: Button.legacy_flavor.solid,
                legacy_statusColor: Button.legacy_statusColor.primary,
                legacy_size: Button.legacy_size.small,
              }}
            >
              Open Resource
            </Anchor>
            {!pathname.startsWith("/assignments/create") && (
              <BookmarkButton
                contentId={props?.content_id}
                streamProperties={streamProperties}
              />
            )}
          </div>
        }
      />
    ),
    generation_genius_video: () => (
      <ArticleCardWithTrackEvent
        {...mappedProps}
        __renderContentType={(angelouProps, contentType) =>
          renderContentType(mappedProps, contentType)
        }
        {...renderTextualProps}
      />
    ),
    infographic: () => <InfographicCardWithTrackEvent {...mappedProps} />,
    interactivevideolink: () => (
      <VideoCardWithTrackEvent
        {...mappedProps}
        source={props?.content_provider?.name}
      />
    ),
    lessonspark: () => {
      return <LessonSparkCardWithTrackEvent {...mappedProps} />;
    },
    LEGACY_ARTICLE: () => <ArticleCardWithTrackEvent {...mappedProps} />,
    poll: () => <PollCardWithTrackEvent {...mappedProps} />,
    standard: () => (
      <StandardCardWithTrackEvent
        {...mappedProps}
        subject={mappedProps.subject_label}
        datePublished={mappedProps.date_published || mappedProps.date_created}
        title={mappedProps.number_enhanced}
        description={mappedProps.statement_combined_description}
      />
    ),
    textset: () => (
      <TextSetCardWithTrackEvent {...mappedProps} {...renderTextualProps} />
    ),
    video: () => <VideoCardWithTrackEvent {...mappedProps} />,
    worksheet: () => <WorksheetCardWithTrackEvent {...mappedProps} />,
  };

  const cardRenderingFn = props.isStacked
    ? stackedDeck[props.content_type]
    : deck[
        isLatestVideo || popularContent ? "lastestVideo" : props.content_type
      ];
  return cardRenderingFn && cardRenderingFn();
});
DeckOfCards.propTypes = propTypes;
DeckOfCards.displayName = "DeckOfCards";
export default DeckOfCards;
